import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AppRouteService } from '@openapi';
import { RouteMapperService } from './route-mapper.service';
import { RawRoute } from '@interfaces/raw-route';

/**
 * * Fetches the raw route configuration from the backend and applies it to
 *  * the Angular router via {@link Router.resetConfig}.
 *  * The mapping from raw JSON to Angular routes is delegated to
 *  * {@link RouteMapperService}.
 */
@Injectable({ providedIn: 'root' })
export class DynamicRouteLoaderService {
  private readonly router = inject(Router);
  private readonly appRouteService = inject(AppRouteService);
  private readonly routeMapper = inject(RouteMapperService);

  /**
   * Loads the dynamic route configuration from the backend and applies it to
   * the Angular router via {@link Router.resetConfig}.
   *
   * @returns An {@link Observable} that completes with `void` once the router
   *          has been reconfigured, or silently recovers on error.
   */
  loadDynamicRoutes(): Observable<void> {
    return this.appRouteService.getAppRoutes().pipe(
      tap((routes: RawRoute[]) => {
        const angularRoutes = this.routeMapper.buildRoutes(routes);
        this.router.resetConfig(angularRoutes);
      }),
      map(() => void 0),
      catchError(err => {
        console.error('Failed to load routes:', err);
        return of(void 0);
      })
    );
  }
}
